import { useState } from "react";
import { Pencil } from "lucide-react";
import { Loader } from "@/components/shared/loader";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useUpdateDocumentLibrary } from "@/features/documents/hooks/useDocumentLibrariesQueries";
import type { DepartmentOption } from "./DocumentsFilters";
import type { DocumentLibrary } from "@/types/document";

const NONE = "NONE";

interface EditLibraryDialogProps {
  library: DocumentLibrary;
  departments: DepartmentOption[];
}

export function EditLibraryDialog({ library, departments }: EditLibraryDialogProps) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState(library.title);
  const [departmentId, setDepartmentId] = useState(library.departmentId ?? "");
  const update = useUpdateDocumentLibrary();

  async function onSave() {
    const trimmed = title.trim();
    if (!trimmed) return;
    try {
      await update.mutateAsync({
        id: library.id,
        data: {
          title: trimmed,
          departmentId: departmentId || null,
        },
      });
      setOpen(false);
    } catch {
      /* handled by mutation */
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) {
          setTitle(library.title);
          setDepartmentId(library.departmentId ?? "");
        }
      }}
    >
      <DialogTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center justify-center text-muted-foreground hover:text-primary transition-colors"
          aria-label="تعديل المكتبة"
        >
          <Pencil className="w-4 h-4" />
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader className="text-right">
          <DialogTitle>تعديل المكتبة</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1 text-right">
            <Label>الاسم</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
          <div className="space-y-1 text-right">
            <Label>الدائرة</Label>
            <Select
              value={departmentId || NONE}
              onValueChange={(v) => setDepartmentId(v === NONE ? "" : v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="اختر الدائرة" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>بدون دائرة</SelectItem>
                {departments.map((d) => (
                  <SelectItem key={d.id} value={d.id}>
                    {d.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button className="w-full" onClick={onSave} disabled={update.isPending}>
            حفظ
            {update.isPending && <Loader className="mr-2" />}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
